import React, { useEffect, useState } from 'react';
import { Input } from '@scuf/common';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import { formatDate } from '../../utils/Functions/shared-utility';
import './date-range-selector.scss';
import TranslationsUtility from '../../utils/translations/TranslationsUtility';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import { useTranslation } from '@scuf/localization';
import { HOCLocalization } from '../../utils/HOC/HOCLocalization';
interface IDateRangeSelector {
  /**
   * Called when a valid date has been selected
   */
  onChange?: (fromDate: Date, toDate: Date) => void;
  onError?: (isError: boolean) => void;
  startTime: string;
  endTime: string;
  maxAllowedDays?: number;
  screenSize: number;
  dateFormat: string;
  dateRangeSelection?: number;
}

const DateRangeSelector = (props: IDateRangeSelector) => {
  const maxAllowedDays = props.maxAllowedDays ? props.maxAllowedDays : 30;
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [localFromDate, setLocalFromDate] = useState(new Date(props.startTime));
  const [localToDate, setLocalToDate] = useState(new Date(props.endTime));
  const [t, lng] = useTranslation();
  const [dateRangeSelection, setDateRangeSelection] = useState(
    props.dateRangeSelection ? props.dateRangeSelection : 0
  );

  const getDefaultDate = () => {
    const convertedDate: Date = new Date();
    convertedDate.setDate(new Date().getDate() - maxAllowedDays);
    convertedDate.setHours(0, 0, 0, 0);
    return convertedDate;
  };

  const validateDateRangeSelection = (fromDate: any, toDate: any) => {
    if (dateRangeSelection <= 0) {
      return false;
    } else {
      let timeDifference = toDate.getTime() - fromDate.getTime();
      let daysDifference = timeDifference / (1000 * 60 * 60 * 24);
      if (daysDifference > 2) {
        daysDifference = daysDifference + 1;
      }
      return daysDifference > dateRangeSelection ? true : false;
    }
  };

  const validateDateRange = () => {
    const isFromDateRangeError =
      localFromDate < getDefaultDate() ? true : false;
    const isToDateRangeError = localToDate < getDefaultDate() ? true : false;
    if (isFromDateRangeError || isToDateRangeError) {
      setError(true);
      setErrorText('outOfRange');
      if (props.onError) {
        props.onError(true);
      }
    } else {
      const tmpFrom = new Date(
        localFromDate.toISOString().replace(/(T)(.*)(Z)/, 'T00:00:00.000Z')
      );
      const tmpTo = new Date(
        localToDate.toISOString().replace(/(T)(.*)(Z)/, 'T00:00:00.000Z')
      );
      if (tmpFrom > tmpTo) {
        setError(true);
        setErrorText('FromToDateRangeError');
        if (props.onError) {
          props.onError(true);
        }
      } else if (validateDateRangeSelection(tmpFrom, tmpTo)) {
        setError(true);
        setErrorText(t('selectDateRange', { days: dateRangeSelection }));
        if (props.onError) {
          props.onError(true);
        }
      } else {
        setError(false);
        if (props.onChange) {
          props.onChange(tmpFrom, tmpTo);
        }
      }
    }
  };

  useEffect(() => {
    validateDateRange();
  }, [localFromDate, localToDate]);

  const fromDatePickerOnChange = (date: Date) => {
    setLocalFromDate(date);
  };

  const toDatePickerOnChange = (date: Date) => {
    setLocalToDate(date);
  };

  const getDatePickerLayout = () => {
    return (
      <>
        <div className="datrange-selector-wrapper">
          <div
            className={
              props.screenSize === 0 || props.screenSize === 1
                ? 'date-selector date-selector-mobile'
                : 'date-selector'
            }
          >
            <div className="date-select-container">
              <span>{t('From')}</span>
              <DayPickerInput
                component={(componentProps: any) => (
                  <Input
                    {...componentProps}
                    placeholder={componentProps.dateFormat}
                    icon="calendar"
                  />
                )}
                key={'header-date-picker-' + props.dateFormat}
                onDayChange={fromDatePickerOnChange}
                value={localFromDate}
                style={error ? { border: '1px solid #F15A4F' } : {}}
                inputProps={{ readOnly: true, ref: null }}
                format={props.dateFormat}
                formatDate={formatDate}
                placeholder={props.dateFormat}
                dayPickerProps={{
                  selectedDays: new Date(localFromDate),
                  locale: TranslationsUtility.getLocale(lng),
                  localeUtils: MomentLocaleUtils,
                  todayButton: t('showToday'),
                  modifiers: {
                    disabled: [
                      {
                        after: new Date(),
                        before: new Date(
                          new Date().setDate(
                            new Date().getDate() - maxAllowedDays
                          )
                        )
                      }
                    ]
                  }
                }}
              />
            </div>
            <InfoTooltip
              content={
                error
                  ? t(errorText)
                  : t('selectUpToDays', { maxDays: props.maxAllowedDays })
              }
              position="bottom right"
              color={error ? '#BE271C' : ''}
            />
          </div>
          <div
            className={
              props.screenSize === 0 || props.screenSize === 1
                ? 'date-selector date-selector-mobile'
                : 'date-selector'
            }
          >
            <div className="date-select-container">
              <span>{t('to')}</span>
              <DayPickerInput
                component={(componentProps: any) => (
                  <Input
                    {...componentProps}
                    placeholder={componentProps.dateFormat}
                    icon="calendar"
                  />
                )}
                key={'header-date-picker-' + props.dateFormat}
                onDayChange={toDatePickerOnChange}
                value={localToDate}
                style={error ? { border: '1px solid #F15A4F' } : {}}
                inputProps={{ readOnly: true, ref: null }}
                format={props.dateFormat}
                formatDate={formatDate}
                placeholder={props.dateFormat}
                dayPickerProps={{
                  selectedDays: new Date(localToDate),
                  locale: TranslationsUtility.getLocale(lng),
                  localeUtils: MomentLocaleUtils,
                  todayButton: t('showToday'),
                  modifiers: {
                    disabled: [
                      {
                        after: new Date()
                      }
                    ]
                  }
                }}
              />
            </div>
            <InfoTooltip
              content={
                error
                  ? t(errorText)
                  : t('selectUpToDays', { maxDays: props.maxAllowedDays })
              }
              position="bottom right"
              color={error ? '#BE271C' : ''}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {props.screenSize === 0 || props.screenSize === 1 ? (
        <div className="date-range-selector-mobile">
          {getDatePickerLayout()}
        </div>
      ) : (
        getDatePickerLayout()
      )}
    </>
  );
};

export default HOCLocalization(DateRangeSelector);
